import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Copy, Link as LinkIcon, Save, Sparkles, CheckCircle2,
    Stethoscope, Loader2, ClipboardList, Pill, FileText, Send, Download, Layers, FileEdit, User, Target, FlaskConical, Calculator,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import SharedFormattedMarkdown from "@/components/FormattedMarkdown";
import AGASection from "@/components/AGASection";
import SendIntakeDialog from "@/components/SendIntakeDialog";
import EvolutionsSection from "@/components/EvolutionsSection";
import ReturnsAndGoalsTab from "@/components/ReturnsAndGoalsTab";
import ConsultationLockPanel from "@/components/ConsultationLockPanel";
import ExamsTimeline from "@/components/ExamsTimeline";
import ProblemList from "@/components/ProblemList";
import ClinicalCalculators from "@/components/ClinicalCalculators";
import PrescriptionDialog from "@/components/PrescriptionDialog";
import VoiceDictateButton from "@/components/VoiceDictateButton";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { AGA_SECTIONS, DEHOSP_CHECKLIST, CONSULTATION_TYPES, STATUS_LABELS, getFormSectionsForType } from "@/lib/aga";
import { TID } from "@/lib/testIds";

const typeLabel = Object.fromEntries(CONSULTATION_TYPES.map((t) => [t.value, t.label]));

export default function ConsultationDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [state, setState] = useState({ consultation: null, patient: null, assessment: null, care_plan: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [tab, setTab] = useState("aga");
    const [aiText, setAiText] = useState("");
    const [patientPlanText, setPatientPlanText] = useState("");
    const [showSend, setShowSend] = useState(false);
    const [showPrescription, setShowPrescription] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/consultations/${id}`);
                setState(data);
                setAiText(data.care_plan?.summary || "");
                setPatientPlanText(data.care_plan?.patient_plan || "");
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [id]);

    const dataObj = state.assessment?.data || {};
    const previousData = state.previous_assessment?.data || null;

    // 24h grace period after lock — inside it the doctor can still edit; after
    // that, everything is readonly and only addendums are allowed.
    const GRACE_MS = 24 * 3600 * 1000;
    const lockedAt = state.consultation?.locked_at ? new Date(state.consultation.locked_at) : null;
    const isLocked = !!lockedAt;
    const inGrace = isLocked && (Date.now() - lockedAt.getTime() < GRACE_MS);
    const readOnly = isLocked && !inGrace;
    const isProfessional = user && ["doctor","admin","nurse","physiotherapist","nutritionist","psychologist","social_worker","occupational_therapist","pharmacist","transpersonal_therapist"].includes(user.role);
    const isDoctor = user && (user.role === "doctor" || user.role === "admin");

    const refresh = async () => {
        try {
            const { data } = await api.get(`/consultations/${id}`);
            setState(data);
            setAiText(data.care_plan?.summary || "");
            setPatientPlanText(data.care_plan?.patient_plan || "");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const updateSection = (sectionId, sectionData) => {
        const next = { ...dataObj, [sectionId]: sectionData };
        setState((s) => ({ ...s, assessment: { ...(s.assessment || {}), data: next } }));
    };

    const saveAssessment = async () => {
        setSaving(true);
        try {
            await api.put(`/consultations/${id}/assessment`, {
                data: state.assessment?.data || {},
                filled_by: "doctor",
            });
            toast.success("Avaliação salva");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const generatePlan = async () => {
        setGenerating(true);
        setAiText("");
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/care-plans/generate`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ consultation_id: id }),
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${resp.status}`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let acc = "";
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                acc += decoder.decode(value, { stream: true });
                setAiText(acc);
            }
            toast.success("Plano gerado pela IA");
        } catch (e) { toast.error(e.message || formatApiError(e)); }
        finally { setGenerating(false); }
    };

    const savePlan = async () => {
        try {
            await api.post("/care-plans", { consultation_id: id, summary: aiText });
            const { data } = await api.get(`/consultations/${id}`);
            setState(data);
            toast.success("Plano de cuidado salvo");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const savePatientPlan = async () => {
        try {
            await api.post("/care-plans", { consultation_id: id, patient_plan: patientPlanText });
            const { data } = await api.get(`/consultations/${id}`);
            setState(data);
            toast.success("Plano para o paciente salvo — já visível no portal do paciente e cuidador.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const copyAiToPatientPlan = () => {
        if (!aiText.trim()) {
            toast.error("Nenhum plano de IA disponível para copiar.");
            return;
        }
        setPatientPlanText(aiText);
        toast.info("Conteúdo copiado. Revise em linguagem acessível antes de salvar.");
    };

    const intakeUrl = useMemo(() => {
        const token = state.consultation?.intake_token;
        return token ? `${window.location.origin}/intake/${token}` : "";
    }, [state.consultation]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(intakeUrl);
            toast.success("Link copiado! Envie ao paciente via WhatsApp ou e-mail.");
        } catch {
            toast.error("Não foi possível copiar. Selecione o link manualmente.");
        }
    };

    const downloadPdf = async () => {
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/care-plans/consultation/${id}/pdf`, { credentials: "include" });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${resp.status}`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `plano_cuidado_${id.slice(0, 8)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF baixado");
        } catch (e) {
            toast.error(e.message || "Falha ao baixar");
        }
    };

    // Handler para o botão de ditado no cabeçalho da consulta: distribui a
    // fala do médico nos campos corretos da AGA. Faz merge com o conteúdo
    // existente, sem sobrescrever o que já estava preenchido fora dos campos
    // tocados pelo ditado.
    const applyDictationToAga = async ({ sections }) => {
        if (!sections || Object.keys(sections).length === 0) return;
        try {
            // 1) Puxa a avaliação atual (ou cria vazia se ainda não existir)
            let currentData = {};
            try {
                const { data: current } = await api.get(`/consultations/${id}/assessment`);
                currentData = current?.data || {};
            } catch (e) {
                // 404 = ainda não criada; segue com objeto vazio
                if (e?.response?.status !== 404) throw e;
            }

            // 2) Merge: para cada seção, preserva campos antigos e sobrescreve
            //    APENAS os que o ditado trouxe. Dentro da mesma seção, se já
            //    havia conteúdo no campo, concatena como novo parágrafo.
            const merged = { ...currentData };
            let filledCount = 0;
            for (const [sectionId, newFields] of Object.entries(sections)) {
                const existing = merged[sectionId] || {};
                const nextSection = { ...existing };
                for (const [fieldKey, newValue] of Object.entries(newFields)) {
                    const prev = (existing[fieldKey] || "").trim();
                    const incoming = (newValue || "").trim();
                    if (!incoming) continue;
                    nextSection[fieldKey] = prev ? `${prev}\n\n${incoming}` : incoming;
                    filledCount += 1;
                }
                merged[sectionId] = nextSection;
            }

            // 3) Persiste a avaliação atualizada
            await api.put(`/consultations/${id}/assessment`, { data: merged });

            // 4) Reflete imediatamente no state local para que a aba AGA já
            //    mostre o conteúdo sem precisar recarregar a página.
            setState((s) => ({
                ...s,
                assessment: { ...(s.assessment || { consultation_id: id }), data: merged },
            }));

            toast.success(`${filledCount} campo(s) preenchido(s) na AGA. Confira cada seção.`);
            // Navega para a aba AGA se não estiver lá
            if (tab !== "aga") setTab("aga");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const downloadSummaryPdf = async () => {        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultations/${id}/summary-pdf`, { credentials: "include" });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${resp.status}`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `resumo_consulta_${id.slice(0, 8)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Resumo baixado — pronto para entregar ao cuidador.");
        } catch (e) {
            toast.error(e.message || "Falha ao baixar resumo");
        }
    };

    const downloadFullRecordPdf = async () => {        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultations/${id}/full-record-pdf`, { credentials: "include" });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${resp.status}`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `prontuario_completo_${id.slice(0, 8)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Prontuário completo baixado.");
        } catch (e) {
            toast.error(e.message || "Falha ao baixar prontuário completo");
        }
    };

    if (loading) return <AppShell><div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-brand" size={32} /></div></AppShell>;
    if (!state.consultation) return <AppShell><p>Consulta não encontrada.</p></AppShell>;

    const c = state.consultation;
    const p = state.patient;
    const isCannabis = c.consultation_type === "telemedicina_canabis";
    const formSections = getFormSectionsForType(c.consultation_type);
    const agaTabLabel = isCannabis ? "Anamnese Endocanabinoide" : "AGA (Avaliação Geriátrica Ampla)";

    return (
        <AppShell>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <button onClick={() => navigate(-1)} className="btn-ghost"><ArrowLeft size={16} /> Voltar</button>
                <div className="flex items-center gap-2 flex-wrap">
                    {["doctor", "nurse", "physiotherapist", "nutritionist", "psychologist",
                      "social_worker", "occupational_therapist", "pharmacist",
                      "transpersonal_therapist"].includes(user?.role) && (
                        <VoiceDictateButton
                            mode="aga"
                            onApply={applyDictationToAga}
                            disabled={saving}
                        />
                    )}
                    <button
                        data-testid="download-summary-pdf-btn"
                        onClick={downloadSummaryPdf}
                        className="btn-secondary"
                        title="PDF resumido em linguagem acessível — pronto para entregar ao cuidador"
                    >
                        <Download size={16} /> Resumo para o cuidador
                    </button>
                    <button
                        data-testid="download-full-record-pdf-btn"
                        onClick={downloadFullRecordPdf}
                        className="btn-primary"
                        title="Prontuário completo — AGA (todas as seções + Impressão Diagnóstica), plano de cuidado, medicações, metas do acompanhamento e evoluções"
                    >
                        <Download size={16} /> Prontuário completo
                    </button>
                </div>
            </div>

            {c.carried_from_consultation_id && (
                <div className="card-earthy mb-4 bg-[#FBF4E5] border-l-4 border-[#8A6A15]" data-testid="carried-forward-banner">
                    <div className="flex items-start gap-3 text-sm text-[#5C4610]">
                        <span className="mt-0.5">🔗</span>
                        <div>
                            <strong>Consulta anterior disponível como referência</strong>
                            {c.carried_from_at ? ` (${new Date(c.carried_from_at).toLocaleDateString("pt-BR")})` : ""}.
                            {" "}Este atendimento é um <strong>novo registro independente</strong> do prontuário —
                            a AGA e o plano de cuidado começam em branco.
                            As <strong>medicações em uso</strong> foram mantidas para reconciliação.
                            Use as abas para consultar sumários da consulta anterior enquanto preenche a atual.
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">{typeLabel[c.consultation_type]}</div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand mb-2">{p?.full_name || "—"}</h1>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="px-2.5 py-0.5 rounded-full bg-alt text-brand-secondary font-medium">{STATUS_LABELS[c.status]}</span>
                    <span className="text-muted-clinical">·</span>
                    <span className="text-[var(--text-secondary)]">Modelo: {c.payment_model === "bundle_payment" ? "Bundle Payment" : "Capitation"}</span>
                    {c.post_hospitalization && (<>
                        <span className="text-muted-clinical">·</span>
                        <span className="text-[var(--brand-accent)] font-medium">Desospitalização recente</span>
                    </>)}
                    {p?.id && (
                        <>
                            <span className="text-muted-clinical">·</span>
                            <Link
                                to={`/patients/${p.id}`}
                                data-testid="open-patient-chart-link"
                                className="text-brand font-medium hover:underline"
                            >
                                Ver prontuário completo →
                            </Link>
                        </>
                    )}
                </div>
            </div>

            {/* Encerramento de consulta / adendos / reabertura */}
            <ConsultationLockPanel
                consultation={state.consultation}
                canEdit={isProfessional}
                isDoctor={isDoctor}
                onChange={refresh}
            />

            {/* Intake link banner — only shown when there is a token (i.e. first consultation) */}
            {!isLocked && intakeUrl && (
            <div className="card-earthy mb-6 bg-alt border-l-4 border-[var(--brand-secondary)]">
                <div className="flex items-center gap-2 text-sm font-semibold text-brand mb-1">
                    <LinkIcon size={16} /> Link do paciente / cuidador
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-3">Envie este link ao paciente para que ele preencha a pré-consulta antes do atendimento. As perguntas que não souber responder poderão ser preenchidas por você durante a consulta.</p>
                <div className="flex flex-col md:flex-row gap-2">
                    <input readOnly value={intakeUrl} className="input-clinical flex-1 font-mono text-xs" />
                    <button data-testid={TID.consultation.copyLinkBtn} onClick={copyLink} className="btn-secondary"><Copy size={14} /> Copiar</button>
                    <button data-testid="send-intake-btn" onClick={() => setShowSend(true)} className="btn-primary"><Send size={14} /> Enviar</button>
                </div>
            </div>
            )}

            {showSend && (
                <SendIntakeDialog consultationId={id} patient={p} onClose={() => setShowSend(false)} />
            )}
            {showPrescription && p && (
                <PrescriptionDialog
                    patient={p}
                    consultationId={id}
                    preselectedMeds={
                        (dataObj?.medicamentos?.medications) || []
                    }
                    activeProblems={[]}
                    onClose={() => setShowPrescription(false)}
                />
            )}

            {/* Tabs */}
            <div className="border-b border-[rgba(30,63,51,0.1)] mb-6 flex flex-wrap gap-1">
                {[
                    { id: "aga", label: agaTabLabel, icon: Stethoscope },
                    { id: "problems", label: "Problemas", icon: ClipboardList },
                    { id: "dehosp", label: "Checklist Desospitalização", icon: ClipboardList, show: c.post_hospitalization && !isCannabis },
                    { id: "exams", label: "Exames", icon: FlaskConical },
                    { id: "calculators", label: "Calculadoras", icon: Calculator },
                    { id: "meds", label: isCannabis ? "Análise de Interações" : "Análise Beers · STOPP/START", icon: Pill },
                    { id: "plan", label: isCannabis ? "Plano Terapêutico" : "Plano de Cuidado", icon: FileText },
                    { id: "returns", label: "Retornos & Metas", icon: Target },
                    { id: "evolutions", label: "Evoluções", icon: FileEdit },
                ].filter(t => t.show !== false).map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={TID.consultation.section(t.id)}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 -mb-px
                                ${active ? "border-brand text-brand" : "border-transparent text-muted-clinical hover:text-brand"}`}
                        >
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "aga" && (
                <ReturnMode
                    sections={formSections}
                    dataObj={dataObj}
                    previousData={previousData}
                    onSectionChange={updateSection}
                    isReturnVisit={!!state.consultation?.carried_from_consultation_id}
                    readOnly={readOnly}
                    saveAssessment={saveAssessment}
                    saving={saving}
                    patientId={p?.id}
                    consultationId={id}
                />
            )}

            {tab === "dehosp" && (
                <DehospChecklist
                    value={dataObj.dehospitalization || {}}
                    onChange={(v) => updateSection("dehospitalization", v)}
                    onSave={saveAssessment}
                    saving={saving}
                />
            )}

            {tab === "meds" && <MedsAnalysis consultationId={id} />}

            {tab === "problems" && p?.id && (
                <ProblemList
                    patientId={p.id}
                    consultationId={id}
                    canEdit={isProfessional && !readOnly}
                />
            )}

            {tab === "exams" && p?.id && (
                <ExamsTimeline
                    patientId={p.id}
                    consultationId={id}
                    canUpload={!readOnly}
                    canEdit={!readOnly && (user?.role === "doctor" || user?.role === "admin" || user?.role === "patient" || user?.role === "caregiver")}
                    embedded={false}
                />
            )}

            {tab === "calculators" && p?.id && <ClinicalCalculators patientId={p.id} consultationId={id} />}

            {tab === "evolutions" && <EvolutionsSection consultationId={id} currentUser={user} />}

            {tab === "returns" && (
                <ReturnsAndGoalsTab
                    patientId={p?.id}
                    currentConsultationId={id}
                    canEdit={user?.role === "doctor" || user?.role === "admin"}
                />
            )}

            {tab === "plan" && (
                <div className="space-y-4">
                    <div className="card-earthy">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <h2 className="font-display text-xl font-medium text-brand">Plano de cuidado</h2>
                                <p className="text-sm text-muted-clinical">Gere um plano assistido por IA (Claude Sonnet 4.5) e revise/edite conforme necessário.</p>
                            </div>
                            <div className="flex gap-2">
                                <button data-testid={TID.consultation.generatePlanBtn} onClick={generatePlan} disabled={generating || readOnly} className="btn-primary">
                                    {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Gerar com IA
                                </button>
                                <button data-testid={TID.consultation.savePlanBtn} onClick={savePlan} disabled={readOnly} className="btn-secondary">
                                    <Save size={16} /> Salvar
                                </button>
                                <button data-testid="download-pdf-btn" onClick={downloadPdf} className="btn-secondary">
                                    <Download size={16} /> PDF
                                </button>
                                <button
                                    data-testid="emit-prescription-btn"
                                    onClick={() => setShowPrescription(true)}
                                    disabled={readOnly}
                                    className="btn-primary"
                                    title="Emitir receita PDF com cabeçalho da clínica"
                                >
                                    <Pill size={16} /> Emitir receita
                                </button>
                                <button data-testid="open-plan-editor-btn" onClick={() => navigate(`/consultations/${id}/plan-editor`)} disabled={readOnly} className="btn-secondary">
                                    <Layers size={16} /> Editor estruturado
                                </button>
                                <button
                                    data-testid="sync-problems-btn"
                                    onClick={async () => {
                                        try {
                                            const { data } = await api.post(`/patients/${p.id}/problems/sync-from-care-plan/${id}`);
                                            toast.success(
                                                `Problem List atualizada: ${data.created} novos, ${data.linked} vinculados${data.skipped ? ", " + data.skipped + " ignorados" : ""}.`
                                            );
                                        } catch (e) { toast.error(formatApiError(e)); }
                                    }}
                                    disabled={readOnly}
                                    className="btn-secondary"
                                    title="Importa os problemas identificados pela IA para o Problem List global do paciente (com CID-10)"
                                >
                                    <ClipboardList size={16} /> Popular Problem List
                                </button>
                            </div>
                        </div>
                        <textarea
                            value={aiText}
                            onChange={(e) => setAiText(e.target.value)}
                            rows={22}
                            readOnly={readOnly}
                            className="input-clinical font-mono text-sm leading-relaxed"
                            placeholder="Clique em &quot;Gerar com IA&quot; para produzir um plano de cuidado personalizado baseado na AGA preenchida."
                        />
                    </div>
                    {aiText && (
                        <div className="card-earthy">
                            <div className="flex items-center gap-2 mb-3 text-brand">
                                <CheckCircle2 size={16} /> <span className="font-medium">Visualização formatada</span>
                            </div>
                            <div className="prose-clinical text-sm">
                                <FormattedMarkdown text={aiText} />
                            </div>
                        </div>
                    )}

                    {/* Plano do médico para o paciente (visível no portal do paciente e cuidador) */}
                    <div className="card-earthy border-l-4 border-[var(--brand-secondary)]">
                        <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                                <h2 className="font-display text-xl font-medium text-brand flex items-center gap-2">
                                    <User size={18} /> Plano para o paciente e cuidador
                                </h2>
                                <p className="text-sm text-muted-clinical mt-1">
                                    Escreva em linguagem acessível o que o paciente, cuidador e família devem fazer.
                                    Este é o <strong>único</strong> conteúdo que aparecerá nos portais deles —
                                    o plano gerado pela IA acima permanece restrito à equipe clínica.
                                </p>
                            </div>
                            <div className="flex flex-col md:flex-row gap-2 shrink-0">
                                <button
                                    data-testid="copy-ai-to-patient-plan-btn"
                                    onClick={copyAiToPatientPlan}
                                    disabled={readOnly}
                                    className="btn-ghost text-xs"
                                    type="button"
                                >
                                    <Copy size={14} /> Copiar da IA
                                </button>
                                <button
                                    data-testid="save-patient-plan-btn"
                                    onClick={savePatientPlan}
                                    disabled={readOnly}
                                    className="btn-primary"
                                    type="button"
                                >
                                    <Save size={16} /> Salvar plano do paciente
                                </button>
                            </div>
                        </div>
                        <textarea
                            data-testid="patient-plan-textarea"
                            value={patientPlanText}
                            onChange={(e) => setPatientPlanText(e.target.value)}
                            rows={14}
                            readOnly={readOnly}
                            className="input-clinical text-sm leading-relaxed"
                            placeholder={"Ex.: \n\n# Suas recomendações\n\n1. Tome os remédios sempre no mesmo horário\n2. Caminhe 30 minutos por dia\n3. Se sentir tontura ou falta de ar, avise o cuidador imediatamente\n\n# Próximo retorno\n15/03/2026"}
                        />
                        {patientPlanText && (
                            <div className="mt-4 pt-4 border-t border-[rgba(30,63,51,0.1)]">
                                <div className="text-xs uppercase tracking-wider text-brand-secondary mb-2 font-medium">
                                    Prévia (assim o paciente verá)
                                </div>
                                <div className="prose-clinical text-sm bg-alt p-3 rounded-lg">
                                    <FormattedMarkdown text={patientPlanText} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </AppShell>
    );
}

function DehospChecklist({ value, onChange, onSave, saving }) {
    const toggle = (sectionId, item) => {
        const section = { ...(value[sectionId] || {}) };
        section[item] = !section[item];
        onChange({ ...value, [sectionId]: section });
    };
    const updateNote = (sectionId, note) => {
        onChange({ ...value, [sectionId]: { ...(value[sectionId] || {}), _note: note } });
    };

    return (
        <div className="space-y-4">
            <div className="card-earthy bg-alt border-l-4 border-[var(--brand-accent)]">
                <h2 className="font-display text-xl font-medium text-brand mb-1">Transição de cuidados pós-hospitalar</h2>
                <p className="text-sm text-[var(--text-secondary)]">Checklist C-R-A-F-A-E-P-D-S baseado no modelo do Dr. Marcos Rodrigo Fernandes.</p>
            </div>
            {DEHOSP_CHECKLIST.map((sec) => {
                const state = value[sec.id] || {};
                const filled = sec.items.filter((it) => state[it]).length;
                return (
                    <div key={sec.id} className="card-earthy">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-lg font-medium text-brand">{sec.title}</h3>
                            <span className="text-xs text-muted-clinical">{filled}/{sec.items.length}</span>
                        </div>
                        <div className="space-y-2">
                            {sec.items.map((item) => (
                                <label key={item} className="flex items-start gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={!!state[item]}
                                        onChange={() => toggle(sec.id, item)}
                                        data-testid={`dehosp-${sec.id}-${item.slice(0,20)}`}
                                        className="mt-1 w-4 h-4"
                                    />
                                    <span className={state[item] ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}>{item}</span>
                                </label>
                            ))}
                        </div>
                        <textarea
                            placeholder="Observações desta seção..."
                            value={state._note || ""}
                            onChange={(e) => updateNote(sec.id, e.target.value)}
                            rows={2}
                            className="input-clinical mt-3"
                        />
                    </div>
                );
            })}
            <div className="flex justify-end">
                <button onClick={onSave} disabled={saving} className="btn-primary">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar checklist
                </button>
            </div>
        </div>
    );
}

function MedsAnalysis({ consultationId }) {
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);

    const analyze = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/care-plans/analyze-medications", { consultation_id: consultationId });
            setText(data.analysis);
            toast.success("Análise concluída");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <div className="card-earthy">
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <h2 className="font-display text-xl font-medium text-brand">Análise Beers 2023 + STOPP/START</h2>
                    <p className="text-sm text-muted-clinical">Analise a reconciliação medicamentosa com IA para identificar medicamentos potencialmente inapropriados e ausências relevantes.</p>
                </div>
                <button onClick={analyze} disabled={loading} data-testid="analyze-meds-btn" className="btn-primary">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Analisar medicamentos
                </button>
            </div>
            {text ? (
                <div className="prose-clinical text-sm">
                    <FormattedMarkdown text={text} />
                </div>
            ) : (
                <div className="text-sm text-muted-clinical bg-alt rounded-lg p-4">
                    Preencha os medicamentos na aba <strong>AGA → Reconciliação Medicamentosa</strong> e clique em <strong>Analisar</strong>.
                </div>
            )}
        </div>
    );
}

function FormattedMarkdown({ text }) {
    // Delegates to shared, DOMPurify-sanitized markdown renderer.
    return <SharedFormattedMarkdown text={text} />;
}

// Sections that stay fully editable when in "consulta de retorno" mode.
// Everything else appears as a compact read-only summary with an "Editar
// mesmo assim" toggle for exceptions.
const RETURN_MODE_EDITABLE_SECTIONS = new Set(["identificacao", "impressao_conduta"]);

function ReturnMode({ sections, dataObj, previousData, onSectionChange, isReturnVisit, readOnly, saveAssessment, saving, patientId, consultationId }) {
    const [enabled, setEnabled] = useState(isReturnVisit);
    // Track which non-editable sections the doctor explicitly expanded to edit.
    const [expanded, setExpanded] = useState({});

    return (
        <div className="space-y-6">
            {isReturnVisit && (
                <div className="card-earthy bg-alt border-l-4 border-brand" data-testid="return-mode-toggle">
                    <label className="flex items-start gap-3 cursor-pointer text-sm">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            className="mt-1 w-4 h-4"
                            data-testid="return-mode-checkbox"
                        />
                        <div>
                            <p className="font-medium text-brand">Modo consulta de retorno</p>
                            <p className="text-xs text-muted-clinical mt-0.5">
                                A consulta atual é um <strong>registro novo e independente</strong>. Nesse modo, cada seção
                                da AGA mostra um <strong>sumário só-leitura da consulta anterior</strong> para você comparar.
                                Apenas <strong>Motivo da consulta / HDA</strong> e <strong>Impressão diagnóstica e conduta</strong>
                                aparecem editáveis por padrão. Clique em <strong>"Preencher nesta consulta"</strong> em qualquer
                                seção que precise atualizar agora (novos achados, mudança de medicação, etc.).
                            </p>
                        </div>
                    </label>
                </div>
            )}
            {previousData && !enabled && (
                <div className="card-earthy bg-[#F4EFE6] border-l-4 border-[#8A6A15] py-3" data-testid="aga-comparison-banner">
                    <div className="text-xs text-[#5C4610] flex items-center gap-2">
                        <span>📊</span>
                        <span>
                            Cada campo mostra um pequeno chip com a variação em relação à consulta anterior.
                            Verde ↑ / Vermelho ↓ indicam a direção da mudança.
                        </span>
                    </div>
                </div>
            )}
            {sections.map((section) => {
                const isEditableSection = !enabled || RETURN_MODE_EDITABLE_SECTIONS.has(section.id) || expanded[section.id];
                if (isEditableSection) {
                    return (
                        <div key={section.id} className="card-earthy" data-testid={`aga-section-${section.id}`}>
                            {enabled && !RETURN_MODE_EDITABLE_SECTIONS.has(section.id) && expanded[section.id] && (
                                <div className="mb-3 flex items-center justify-between gap-2 text-xs">
                                    <span className="text-[var(--brand-accent)] font-medium">Preenchendo nesta consulta — os dados serão salvos apenas neste atendimento.</span>
                                    <button
                                        onClick={() => setExpanded((e) => ({ ...e, [section.id]: false }))}
                                        className="btn-ghost text-xs"
                                        data-testid={`collapse-section-${section.id}`}
                                    >Ver sumário anterior</button>
                                </div>
                            )}
                            <AGASection
                                section={section}
                                value={dataObj[section.id] || {}}
                                previous={previousData?.[section.id] || null}
                                onChange={(v) => onSectionChange(section.id, v)}
                                hint={section.forPatient ? "Seção sugerida para pré-preenchimento pelo paciente" : "Seção clínica — preenchida pelo médico"}
                                patientId={patientId}
                                consultationId={consultationId}
                            />
                        </div>
                    );
                }
                // Read-only summary card — mostra os dados da CONSULTA ANTERIOR
                // como referência, já que a consulta atual começa em branco.
                return (
                    <SectionSummary
                        key={section.id}
                        section={section}
                        data={previousData?.[section.id] || {}}
                        onEdit={() => setExpanded((e) => ({ ...e, [section.id]: true }))}
                        disabled={readOnly}
                    />
                );
            })}
            <div className="sticky bottom-4 flex justify-end">
                <button onClick={saveAssessment} disabled={saving || readOnly} data-testid="save-aga-btn" className="btn-primary shadow-lg">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar avaliação
                </button>
            </div>
        </div>
    );
}

function SectionSummary({ section, data, onEdit, disabled }) {
    const filled = section.fields.filter((f) => {
        const v = data?.[f.key];
        return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
    });
    return (
        <div className="card-earthy bg-alt/40 border border-dashed border-[var(--border-organic)]" data-testid={`aga-summary-${section.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-lg font-medium text-brand">{section.title}</h3>
                    <p className="text-[11px] uppercase tracking-widest text-[#8A6A15] font-semibold mt-0.5">
                        Registro da consulta anterior — somente leitura
                    </p>
                    {section.subtitle && <p className="text-xs text-muted-clinical mt-0.5">{section.subtitle}</p>}
                </div>
                {!disabled && (
                    <button
                        onClick={onEdit}
                        data-testid={`edit-section-${section.id}`}
                        className="btn-ghost text-xs shrink-0"
                        title="Abrir esta seção em branco para preencher nesta consulta"
                    >
                        <FileEdit size={12} /> Preencher nesta consulta
                    </button>
                )}
            </div>
            {filled.length === 0 ? (
                <p className="text-sm text-muted-clinical italic">Sem informações na consulta anterior.</p>
            ) : (
                <dl className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {filled.map((f) => {
                        const v = data[f.key];
                        const display = Array.isArray(v)
                            ? v.map((it) => (typeof it === "object" ? JSON.stringify(it) : String(it))).join(", ")
                            : typeof v === "object" ? JSON.stringify(v) : String(v);
                        return (
                            <div key={f.key} className="md:col-span-2">
                                <dt className="text-[11px] uppercase tracking-widest text-brand-secondary font-medium">{f.label}</dt>
                                <dd className="text-[var(--text-primary)] whitespace-pre-wrap">{display}</dd>
                            </div>
                        );
                    })}
                </dl>
            )}
        </div>
    );
}
